// Snooker's UI wiring. One screen only (no number-assignment phase, unlike
// Killer) - the shared mount() container goes straight to the live board.
//
// Rules: 15 reds (wedges 1-15, one per red - RED_NUMBERS) alternate with any
// color (yellow 16, green 17, brown 18, blue 19, pink 20, black bull - each
// worth its own real snooker point value) until the reds run out, then the
// six colors must be potted in strict order (yellow -> green -> brown ->
// blue -> pink -> black). A double/treble never removes more than the one
// ball actually hit, but whether it multiplies that ball's score (2x/3x)
// instead of scoring its plain value is the setup screen's own Doubles/
// triples choice (multiplyScores, off by default - real snooker has no
// such thing, a ball's worth is always fixed no matter how it's potted).
//
// A turn ends on either of two things (see applyThrow's returned outcome):
//   - a FOUL: hitting a ball that's still in play (on the board, not yet
//     potted) but wasn't the one due - every other player at the table gets
//     its value, or MIN_FOUL, whichever is higher (real snooker only ever
//     has the one opponent to award it to; with more players, everyone else
//     benefits from someone else's foul).
//   - a MISS: hitting a target already out of play (a red already potted,
//     or - during the colors-only endgame - any red at all, or a color
//     that's already had its turn) - no score changes either way.
// The player can also end their own turn early with the End Turn button
// (handled identically to a miss - no score change), e.g. once they've
// thrown all the real darts they're going to for this visit. Ending a turn
// this way is undoable exactly like a dart is (see endTurn()).
//
// How many darts a turn can run to before THAT also ends it is the setup
// screen's own Turn mode choice (turnMode, see definition.js's
// configRows): 'break' (the default) is real snooker's own "break"
// rule - unlike every other game here, a pot never ends the turn on its
// own, so a turn can run to any number of darts. 'alternate' caps it at 3,
// same as every other game here, in addition to the foul/miss/End-Turn
// endings above. It also ends a turn the moment a red-then-color pair
// completes during the reds phase (not just the red half) - a player can
// never be left holding a just-potted red with no dart left for its
// color - except for the free color taken right after the very last red,
// which moves play into the colors-only phase instead of ending anything.
// See handleGameDart().
// Highest score once black is potted wins.
import snooker from './definition.js';
import { RED_NUMBERS, COLORS, RED_COLOR, TOTAL_REDS, MIN_FOUL } from './definition.js';
import { mountDartboardFullBoard, drawDartMark } from '../../components/dartboard-input.js';
import { configRowDefault } from '../shared/config-rows.js';
import { snookerObserverBoard } from './observer-board.js';

let currentPlayers = [];
let container = null;
let onChangeCallback = null;
let toastTimer = null;
// Set once at mount() from the setup screen's Doubles/triples and Turn mode
// options (see definition.js's configRows) - never touched again, same as
// Killer's own lives/ring, so neither is part of getState()/setState()
// either; a client joining mid-match gets both from the same envelope that
// mounts the game for it in the first place.
let multiplyScores = false;
let turnMode = 'break'; // 'break' | 'alternate' - see handleGameDart()
let isObserver = false;

const RED_SET = new Set(RED_NUMBERS);
const COLOR_BY_NUMBER = new Map(COLORS.map(c => [c.number, c]));
const BLACK_COLOR = COLOR_BY_NUMBER.get(25).color;
// Every wedge and the bull, mapped to its own real ball color - drawn as a
// dot on top of each wedge (see paintBoard below) rather than recoloring
// the whole wedge, so the board still reads as a dartboard first and a
// rack of snooker balls second.
const BALL_COLOR_BY_NUMBER = new Map([
    ...RED_NUMBERS.map(n => [n, RED_COLOR]),
    ...COLORS.map(c => [c.number, c.color])
]);

// A real dartboard's wedges, in clockwise order starting from the top -
// dartboard.svg's own number labels are authored in this exact order too
// (label i sits at angle i*18deg clockwise from vertical), which is what
// lets ballDotPosition below place a dot in the middle of any wedge without
// parsing its actual path geometry.
const WEDGE_ORDER = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];
const BOARD_CENTER = 250;
const BALL_DOT_RADIUS_FROM_CENTER = 162; // inside the outer single band (~137.5-187.5)
const BALL_DOT_SIZE = 19;

function ballDotPosition(number) {
    const angleDeg = WEDGE_ORDER.indexOf(number) * 18;
    const angleRad = (angleDeg * Math.PI) / 180;
    return {
        x: BOARD_CENTER + BALL_DOT_RADIUS_FROM_CENTER * Math.sin(angleRad),
        y: BOARD_CENTER - BALL_DOT_RADIUS_FROM_CENTER * Math.cos(angleRad)
    };
}

// --- game state ---
let scores = {}; // player -> points
let potHistory = {}; // player -> [ball color hex, ...], oldest to newest - see recordPot()
let redsRemaining = TOTAL_REDS;
let removedReds = []; // wedge numbers (1-15) already potted at least once - permanently dimmed, no ball drawn
let awaitingColor = false; // reds phase only: true once a red's been potted and a color is due next
let nextColorIndex = 0; // colors phase only: index into COLORS for the next ball due
let phase = 'reds'; // 'reds' | 'colors'
let activeIndex = 0;
let dartsThisTurn = 0;
let turnDarts = [];
let turnStartScore = 0; // scores[activePlayer] as of the start of this turn - Break mode's "current break" is just the gain since then
let matchComplete = false;
let history = [];
let toastSeq = 0;
let lastToast = null; // { msg, kind, seq } | null
let lastAppliedToastSeq = 0;

function hasProgress() {
    return history.length > 0;
}

function showToast(msg, kind) {
    toastSeq += 1;
    lastToast = { msg, kind, seq: toastSeq };
    lastAppliedToastSeq = toastSeq;
    renderToast(msg, kind);
}

function renderToast(msg, kind) {
    const el = document.getElementById('snookerToast');
    if (!el) return;
    el.textContent = msg;
    el.className = 'snooker-toast show ' + (kind || '');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 2000);
}

function hideToast() {
    const el = document.getElementById('snookerToast');
    if (el) el.classList.remove('show');
    clearTimeout(toastTimer);
}

// Dims every wedge/label to FULL_DIM except the numbers currently relevant
// (targetNumbers), which stay at full brightness and get a pulsing class on
// every ring of their section - unlike Killer, any multiplier counts here,
// so the whole wedge is the target, not just one ring.
const FULL_DIM = '0.3';

// Plain weighted-RGB relative luminance (0-1) - not perceptually exact, but
// plenty to sort "needs the light pulse" from "needs the dark one" (see
// paintBoard) the same way the dartboard's own black/white wedges already
// did by comparing a hardcoded fill value.
function isLightColor(hex) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 > 0.5;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

// The plain colored circle for a ball - reds get only this; colors (see
// createValueBadge below) get this plus a value badge on top.
function createBallDot(x, y, color) {
    const dot = document.createElementNS(SVG_NS, 'circle');
    dot.setAttribute('cx', x);
    dot.setAttribute('cy', y);
    dot.setAttribute('r', BALL_DOT_SIZE);
    dot.setAttribute('fill', color);
    return dot;
}

// A small white circle with the ball's point value on it, same idea as a
// numbered pool ball - only for the six colors (and black, on the bull),
// never the plain reds, which is what tells the two apart on the board at
// a glance in addition to their color.
function createValueBadge(x, y, value) {
    const g = document.createElementNS(SVG_NS, 'g');
    const badge = document.createElementNS(SVG_NS, 'circle');
    badge.setAttribute('cx', x);
    badge.setAttribute('cy', y);
    badge.setAttribute('r', BALL_DOT_SIZE * 0.55);
    badge.setAttribute('fill', '#FFFFFF');
    g.appendChild(badge);
    const text = document.createElementNS(SVG_NS, 'text');
    text.setAttribute('x', x);
    text.setAttribute('y', y);
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('dominant-baseline', 'central');
    text.setAttribute('font-size', BALL_DOT_SIZE * 0.75);
    text.setAttribute('font-weight', '700');
    text.setAttribute('fill', '#000000');
    text.textContent = String(value);
    g.appendChild(text);
    return g;
}

// The dartboard keeps its own normal black/white/red/green colors - only
// dimming (for anything not currently a target) is applied on top - and a
// dot in the real color of the snooker ball that wedge represents (see
// BALL_COLOR_BY_NUMBER/ballDotPosition) sits over each one, with a numbered
// value badge added for the six colors (not the plain reds). A target's dot
// also gets a pulsing highlight (light or dark pulse chosen by that ball
// color's own brightness).
function paintBoard(mount, targetNumbers) {
    const svg = mount.querySelector('svg');
    if (!svg) return;
    const targets = new Set(targetNumbers);
    // Colors phase only: each color is potted exactly once, in strict
    // order, so every color before nextColorIndex is gone for good - same
    // "no ball left to draw" treatment as an already-potted red gets below.
    // During the reds phase a potted color is always respotted (real
    // snooker's own rule), so this stays empty there.
    const removedColorNumbers = phase === 'colors'
        ? new Set(COLORS.slice(0, nextColorIndex).map(c => c.number))
        : new Set();
    svg.querySelectorAll('g[class^="section-"]').forEach(section => {
        const match = /section-(\d+)/.exec(section.getAttribute('class') || '');
        if (!match) return;
        const number = Number(match[1]);
        const isTarget = targets.has(number);
        const opacity = isTarget ? '' : FULL_DIM;
        section.querySelectorAll('.single, .double-ring, .triple-ring').forEach(path => {
            path.style.opacity = opacity;
        });

        // A red already potted once has no ball left to draw - just the
        // dimmed, empty black wedge from here on (see removedReds).
        if (RED_SET.has(number) && removedReds.includes(number)) return;
        // Same for a color already potted during the colors-only endgame.
        if (removedColorNumbers.has(number)) return;

        const ballColor = BALL_COLOR_BY_NUMBER.get(number);
        const isDark = !isLightColor(ballColor);
        const { x, y } = ballDotPosition(number);
        const icon = document.createElementNS(SVG_NS, 'g');
        icon.classList.add('snooker-ball-icon');
        icon.appendChild(createBallDot(x, y, ballColor));
        const color = COLOR_BY_NUMBER.get(number);
        if (color) icon.appendChild(createValueBadge(x, y, color.value));
        icon.style.opacity = opacity;
        icon.style.pointerEvents = 'none';
        icon.classList.toggle('snooker-target-wedge', isTarget && !isDark);
        icon.classList.toggle('snooker-target-wedge-dark', isTarget && isDark);
        section.appendChild(icon);
    });
    // Black lives on the bull (see COLORS) - outer bull and bullseye keep
    // their own normal colors (green/red) and are treated identically as
    // potting attempts, so both light up together; a value badge (7) sits
    // on top the same way a wedge color's does.
    const bullTarget = targets.has(25);
    const bullIsDark = !isLightColor(BLACK_COLOR);
    [svg.querySelector('.outer-bull'), svg.querySelector('.bullseye')].forEach(el => {
        if (!el) return;
        el.style.opacity = bullTarget ? '' : FULL_DIM;
        el.classList.toggle('snooker-target-wedge', bullTarget && !bullIsDark);
        el.classList.toggle('snooker-target-wedge-dark', bullTarget && bullIsDark);
    });
    const bullBadge = createValueBadge(BOARD_CENTER, BOARD_CENTER, COLOR_BY_NUMBER.get(25).value);
    bullBadge.classList.add('snooker-ball-icon');
    bullBadge.style.opacity = bullTarget ? '' : FULL_DIM;
    bullBadge.style.pointerEvents = 'none';
    bullBadge.classList.toggle('snooker-target-wedge', bullTarget && !bullIsDark);
    bullBadge.classList.toggle('snooker-target-wedge-dark', bullTarget && bullIsDark);
    svg.appendChild(bullBadge);
    // The dartboard's own number labels (1-20) - not our own value-badge
    // text (also numeric, e.g. "2" on yellow's badge), which would
    // otherwise collide with this same "is my number a target" opacity
    // check for a completely unrelated reason.
    svg.querySelectorAll('text').forEach(label => {
        if (label.closest('.snooker-ball-icon')) return;
        const num = Number(label.textContent.trim());
        if (!Number.isNaN(num)) label.style.opacity = targets.has(num) ? '' : FULL_DIM;
    });
}

function colorLabel(number) {
    return COLOR_BY_NUMBER.get(number)?.name || String(number);
}

function requiredLabel() {
    if (phase === 'colors') return `${colorLabel(COLORS[nextColorIndex].number)} (color)`;
    return awaitingColor ? 'any color' : 'a red';
}

function targetNumbers() {
    if (phase === 'colors') return [COLORS[nextColorIndex].number];
    // Already-potted reds (removedReds) are excluded - there's no ball left
    // on that wedge to aim for any more (see paintBoard).
    return awaitingColor ? COLORS.map(c => c.number) : RED_NUMBERS.filter(n => !removedReds.includes(n));
}

// Appends one dot's worth of pot history for `player`, in `colorHex` -
// see the .snooker-recent-mini strip in renderAll().
function recordPot(player, colorHex) {
    potHistory[player].push(colorHex);
}

// Every other player at the table - real snooker only ever has the one
// opponent, but with 3+ players a foul's points go to everyone else, not
// just whoever's up next.
function foulOpponents(thrower) {
    return currentPlayers.filter(p => p !== thrower);
}

// Names foul points went to, for the toast - the single other player's
// name in a 2-player match, or "everyone else" once there's more than one.
function foulLabel(opponents) {
    return opponents.length === 1 ? opponents[0] : 'everyone else';
}

// The whole rules engine for one dart. Mutates scores/redsRemaining/
// awaitingColor/phase/nextColorIndex directly, shows a toast describing
// what happened, and returns 'pot' (turn continues), 'foul' or 'miss' (both
// end the turn - see handleGameDart) so the caller never has to duplicate
// this file's own idea of what counts as which.
function applyThrow(thrower, number, mult) {
    const isRedNumber = RED_SET.has(number);
    const redLive = isRedNumber && !removedReds.includes(number);
    const redDead = isRedNumber && !redLive;
    const color = COLOR_BY_NUMBER.get(number);

    if (phase === 'reds') {
        if (!awaitingColor) {
            if (redLive) {
                // A double/treble only ever removes the one red actually
                // hit - never more than that. Whether it scores extra for
                // the ring (2/3) instead of the usual 1 is the setup
                // screen's Doubles/triples choice (multiplyScores).
                const points = multiplyScores ? mult : 1;
                redsRemaining -= 1;
                scores[thrower] += points;
                removedReds.push(number);
                recordPot(thrower, RED_COLOR);
                awaitingColor = true;
                showToast(`${thrower} pots a red for ${points} (${redsRemaining} left)`, 'pot');
                return 'pot';
            }
            if (color) {
                // Still on the board, just not the one due - a foul, not a
                // miss (see redDead below for the actual "nothing there").
                const opponents = foulOpponents(thrower);
                const foul = Math.max(MIN_FOUL, color.value);
                opponents.forEach(p => { scores[p] += foul; });
                showToast(`Foul! ${thrower} hits a color before a red - ${foulLabel(opponents)} +${foul}`, 'foul');
                return 'foul';
            }
            // redDead - nothing left on that wedge to pot.
            showToast(`${thrower} misses`, 'miss');
            return 'miss';
        }

        // awaiting a color
        if (color) {
            const points = multiplyScores ? color.value * mult : color.value;
            scores[thrower] += points;
            recordPot(thrower, color.color);
            if (redsRemaining <= 0) {
                phase = 'colors';
                nextColorIndex = 0;
                showToast(`${thrower} pots ${color.name} for ${points} - reds are cleared, colors only from here`, 'pot');
            } else {
                awaitingColor = false;
                showToast(`${thrower} pots ${color.name} (+${points})`, 'pot');
            }
            return 'pot';
        }
        if (redLive) {
            // Still on the board (not yet potted this pass), just not due
            // right now - a foul, same reasoning as the color-before-red
            // case above.
            const opponents = foulOpponents(thrower);
            opponents.forEach(p => { scores[p] += MIN_FOUL; });
            showToast(`Foul! ${thrower} hits a red when a color was due - ${foulLabel(opponents)} +${MIN_FOUL}`, 'foul');
            return 'foul';
        }
        // redDead
        showToast(`${thrower} misses`, 'miss');
        return 'miss';
    }

    // phase === 'colors': strict order, only the next ball due can score.
    // Reds don't exist here at all any more - always a miss.
    const dueColor = COLORS[nextColorIndex];
    if (number === dueColor.number) {
        const points = multiplyScores ? dueColor.value * mult : dueColor.value;
        scores[thrower] += points;
        recordPot(thrower, dueColor.color);
        nextColorIndex += 1;
        if (nextColorIndex >= COLORS.length) {
            matchComplete = true;
            showToast(`${thrower} pots Black for ${points}! Game over`, 'pot');
        } else {
            showToast(`${thrower} pots ${dueColor.name} (+${points})`, 'pot');
        }
        return 'pot';
    }
    if (color) {
        const colorIndex = COLORS.indexOf(color);
        if (colorIndex > nextColorIndex) {
            // Still waiting its turn later in the order - a foul, not a miss.
            const opponents = foulOpponents(thrower);
            const foul = Math.max(MIN_FOUL, color.value);
            opponents.forEach(p => { scores[p] += foul; });
            showToast(`Foul! ${thrower} hits ${color.name} out of order - ${foulLabel(opponents)} +${foul}`, 'foul');
            return 'foul';
        }
        // colorIndex < nextColorIndex - already potted earlier, long gone.
        showToast(`${thrower} misses`, 'miss');
        return 'miss';
    }
    showToast(`${thrower} misses`, 'miss');
    return 'miss';
}

function checkWin() {
    if (!matchComplete) return;
    const ranked = snooker.rankPlayers(currentPlayers, scores);
    showToast(`${ranked[0]} wins with ${scores[ranked[0]]}!`, 'win');
}

function advanceTurn() {
    dartsThisTurn = 0;
    turnDarts = [];
    activeIndex = (activeIndex + 1) % currentPlayers.length;
    turnStartScore = scores[currentPlayers[activeIndex]];
    // Every turn starts on a red - a pending "pot a red, now pot a color"
    // obligation never survives being handed to someone else. Potting a
    // red and then missing/fouling the color, or just ending the turn
    // there, throws that pending color away rather than owing it to
    // whoever's up next (a deliberate simplification, not real snooker's
    // own "the color's still on" rule). Meaningless once reds are gone, so
    // only touched during the reds phase.
    if (phase === 'reds') {
        // The one exception: if that pending color was THE final one (the
        // last red is already gone), there's no red left to fall back to -
        // "on: a red" with zero red targets on the board would strand the
        // next player entirely. Move straight into the colors-only endgame
        // instead of getting stuck asking for a ball that no longer exists.
        if (awaitingColor && redsRemaining <= 0) {
            phase = 'colors';
            nextColorIndex = 0;
        }
        awaitingColor = false;
    }
}

// A player-initiated pass, identical to a miss - no score change, turn
// just moves on - for whenever they'd rather stop than keep throwing (e.g.
// they're out of real darts for this visit). Snapshotted first exactly like
// a dart is, so it undoes the same way (see undoLastDart).
function endTurn() {
    if (matchComplete) return;
    history.push(snapshotGame());
    showToast(`${currentPlayers[activeIndex]} ends their turn`, '');
    advanceTurn();
    renderAll();
    notifyChange();
}

function snapshotGame() {
    return JSON.parse(JSON.stringify({
        scores, potHistory, redsRemaining, removedReds, awaitingColor, nextColorIndex, phase,
        activeIndex, dartsThisTurn, turnDarts, turnStartScore, matchComplete
    }));
}

function handleGameDart(number, mult, point) {
    if (matchComplete) return;
    const activePlayer = currentPlayers[activeIndex];
    history.push(snapshotGame());

    // Captured before applyThrow mutates them, to tell "this pot completed
    // a red-then-color pair" apart from every other kind of pot below.
    const wasAwaitingColor = awaitingColor;
    const wasInRedsPhase = phase === 'reds';

    const outcome = applyThrow(activePlayer, number, mult);

    turnDarts.push({ number, mult, x: point && point.x, y: point && point.y });
    dartsThisTurn += 1;
    checkWin();
    // A pot keeps the same player throwing, same as a real snooker break -
    // only a foul or a miss (see applyThrow) hands the turn over on its
    // own. In Alternate turns mode, 3 darts does too, pot or not - same cap
    // as every other game here.
    //
    // Alternate turns mode has one more way a turn ends early: potting a
    // color that completes a red-then-color pair during the reds phase - a
    // full pair, not just the red half - ends the turn right there, so a
    // player can never be left holding a completed red with no dart left
    // for its color (see the earlier bug this guards against). The
    // exception is the free color taken right after the very last red -
    // that one moves play into the colors-only phase instead of ending
    // anything, so it's excluded by checking the phase is STILL 'reds'
    // after the throw.
    const completedRedColorPair = wasInRedsPhase && wasAwaitingColor && outcome === 'pot' && phase === 'reds';
    const turnIsOver = outcome !== 'pot'
        || (turnMode === 'alternate' && (dartsThisTurn >= 3 || completedRedColorPair));
    if (!matchComplete && turnIsOver) advanceTurn();

    renderAll();
    notifyChange();
}

function undoLastDart() {
    const snap = history.pop();
    if (!snap) return;
    scores = snap.scores;
    potHistory = snap.potHistory;
    redsRemaining = snap.redsRemaining;
    removedReds = snap.removedReds;
    awaitingColor = snap.awaitingColor;
    nextColorIndex = snap.nextColorIndex;
    phase = snap.phase;
    activeIndex = snap.activeIndex;
    dartsThisTurn = snap.dartsThisTurn;
    turnDarts = snap.turnDarts;
    turnStartScore = snap.turnStartScore;
    matchComplete = snap.matchComplete;
    hideToast();
    renderAll();
    notifyChange({ suppressComplete: true });
}

function boardScreenHTML(players, observer) {
    // The observer's wall display is its own layout (see
    // renderObserverBoard), not the phone board minus its Undo button - it
    // keeps only the toast, which still carries foul/pot feedback.
    if (observer) {
        return `
            <div class="obs-board obs-sn" id="snookerObsBoard"></div>
            <div class="snooker-toast" id="snookerToast"></div>
        `;
    }
    // Same card shape as 501's own player element (name, the big score, a
    // sub-line below it, and a bordered mini-column on the right) - the
    // sub-line shows Break mode's running break, same styling/layout as
    // 501's own checkout hint (see .snooker-player-break/.x01-player-checkout).
    const cards = players.map((player, i) => `
        <div class="snooker-player" id="snooker-player-${i}">
            <i class="fa-regular fa-circle-dot snooker-turn-icon" id="snooker-badge-${i}" style="visibility:hidden"></i>
            <div class="snooker-player-main">
                <div class="snooker-player-name">${player}</div>
                <div class="snooker-player-score" id="snooker-score-${i}">0</div>
                <div class="snooker-player-break" id="snooker-break-${i}"></div>
            </div>
            <div class="snooker-recent-mini" id="snooker-recent-mini-${i}"></div>
        </div>
    `).join('');

    return `
        <div class="snooker-board">
            <div class="snooker-scoreboard">${cards}</div>
            <div class="snooker-play-area">
                <div class="snooker-board-header-row">
                    ${observer ? '' : `
                    <button class="grid-icon-btn-snooker-pill snooker-corner-undo" type="button" id="snookerBoardUndoBtn" disabled title="Undo last dart" aria-label="Undo last dart">
                        <i class="fa-solid fa-rotate-left"></i> Undo
                    </button>
                    <button class="grid-icon-btn-snooker-pill snooker-corner-endturn" type="button" id="snookerEndTurnBtn" title="End turn" aria-label="End turn">
                        End Turn <i class="fa-solid fa-forward-step"></i>
                    </button>`}
                    <div class="snooker-board-header">
                        <div class="snooker-turn-row" id="snookerTurnRow"></div>
                        <div class="snooker-status-row" id="snookerStatusRow"></div>
                    </div>
                </div>
                <div class="snooker-hint" id="snookerHint"></div>
                <div class="dartboard-mount snooker-dart-mount" id="snookerBoardDartMount"></div>
            </div>
        </div>
        <div class="snooker-toast" id="snookerToast"></div>
    `;
}

function renderObserverBoard() {
    const board = document.getElementById('snookerObsBoard');
    if (!board) return;
    const nextColor = COLORS[Math.min(nextColorIndex, COLORS.length - 1)];
    const { html, classes } = snookerObserverBoard({
        players: currentPlayers,
        scores,
        activeIndex,
        matchComplete,
        turnMode,
        currentBreak: scores[currentPlayers[activeIndex]] - turnStartScore,
        dartNumber: dartsThisTurn + 1,
        onKind: phase === 'colors' ? 'next' : awaitingColor ? 'color' : 'red',
        onLabel: phase === 'colors' ? nextColor.name : awaitingColor ? 'Any color' : 'Red',
        targets: matchComplete ? [] : targetNumbers(),
        removedReds,
        colorsGone: new Set(phase === 'colors' ? COLORS.slice(0, nextColorIndex).map(c => c.number) : []),
        nextColor,
        subtitle: `<b>${turnMode === 'break' ? 'Break mode' : 'Alternate turns'}</b> &middot; ${multiplyScores ? 'doubles/triples multiply' : 'fixed values'}`
    });
    Object.entries(classes).forEach(([name, on]) => board.classList.toggle(name, on));
    board.innerHTML = html;
}

function renderAll() {
    if (isObserver) {
        renderObserverBoard();
        return;
    }
    currentPlayers.forEach((player, i) => {
        const isTurn = i === activeIndex && !matchComplete;

        const card = document.getElementById(`snooker-player-${i}`);
        if (card) card.classList.toggle('active', isTurn);

        const badge = document.getElementById(`snooker-badge-${i}`);
        if (badge) badge.style.visibility = isTurn ? 'visible' : 'hidden';

        const scoreEl = document.getElementById(`snooker-score-${i}`);
        if (scoreEl) scoreEl.textContent = scores[player];

        const breakEl = document.getElementById(`snooker-break-${i}`);
        if (breakEl) breakEl.textContent = (turnMode === 'break' && isTurn) ? `Current break: ${scores[player] - turnStartScore}` : '';

        const recentMini = document.getElementById(`snooker-recent-mini-${i}`);
        if (recentMini) {
            recentMini.innerHTML = potHistory[player]
                .map(colorHex => `<span class="snooker-pot-dot" style="background-color:${colorHex}"></span>`)
                .join('');
            recentMini.scrollLeft = recentMini.scrollWidth;
        }
    });

    const undoBtn = document.getElementById('snookerBoardUndoBtn');
    if (undoBtn) undoBtn.disabled = history.length === 0;

    const endTurnBtn = document.getElementById('snookerEndTurnBtn');
    if (endTurnBtn) endTurnBtn.disabled = matchComplete;

    const turnRow = document.getElementById('snookerTurnRow');
    if (turnRow) {
        // "of 3" only in Alternate turns mode - Break mode keeps going for
        // as long as it keeps potting (see the file-top comment), so
        // there's no fixed dart count to measure it against there.
        const dartCount = turnMode === 'alternate' ? `${dartsThisTurn + 1} of 3` : `${dartsThisTurn + 1}`;
        turnRow.innerHTML = matchComplete
            ? 'Game over'
            : `<b>${currentPlayers[activeIndex]}</b>'s throw &middot; dart <b>${dartCount}</b>`;
    }

    const statusRow = document.getElementById('snookerStatusRow');
    if (statusRow) {
        statusRow.textContent = matchComplete
            ? ''
            : phase === 'colors'
                ? `Colors only - next up: ${colorLabel(COLORS[nextColorIndex].number)}`
                : `${redsRemaining} reds left - on: ${requiredLabel()}`;
    }

    const hint = document.getElementById('snookerHint');
    if (hint) hint.textContent = matchComplete ? '' : (isObserver ? '' : 'Tap the dartboard for this throw');

    const dartMount = document.getElementById('snookerBoardDartMount');
    if (dartMount) {
        if (matchComplete) {
            dartMount.innerHTML = '';
        } else {
            mountDartboardFullBoard(dartMount, { onTap: handleGameDart, readOnly: isObserver });
            paintBoard(dartMount, targetNumbers());
            // A break can run to many darts now (see the file-top comment) -
            // only the 2 most recent marks are ever drawn (the older one
            // dimmed, the newest at full opacity), instead of a
            // still-growing cluster of every dart landed this break. Undo
            // needs nothing special here - it just restores turnDarts to a
            // shorter array (see undoLastDart), and this redraws from
            // whatever that now is on the very next renderAll().
            turnDarts.slice(-2).forEach(({ x, y }, i, lastTwo) => {
                if (x === undefined) return;
                drawDartMark(dartMount, x, y);
                const marks = dartMount.querySelectorAll('.dart-tap-mark');
                marks[marks.length - 1].style.opacity = i === lastTwo.length - 1 ? '' : FULL_DIM;
            });
        }
    }
}

function notifyChange({ suppressComplete = false } = {}) {
    if (!onChangeCallback) return;
    onChangeCallback({ scores: { ...scores }, complete: !suppressComplete && matchComplete });
}

function getState() {
    return JSON.parse(JSON.stringify({
        scores, potHistory, redsRemaining, removedReds, awaitingColor, nextColorIndex, phase,
        activeIndex, dartsThisTurn, turnDarts, turnStartScore, matchComplete, lastToast
    }));
}

function setState(snapshot) {
    scores = snapshot.scores;
    potHistory = snapshot.potHistory;
    redsRemaining = snapshot.redsRemaining;
    removedReds = snapshot.removedReds;
    awaitingColor = snapshot.awaitingColor;
    nextColorIndex = snapshot.nextColorIndex;
    phase = snapshot.phase;
    activeIndex = snapshot.activeIndex;
    dartsThisTurn = snapshot.dartsThisTurn;
    turnDarts = snapshot.turnDarts;
    turnStartScore = snapshot.turnStartScore ?? scores[currentPlayers[activeIndex]];
    matchComplete = snapshot.matchComplete;
    if (snapshot.lastToast && snapshot.lastToast.seq !== lastAppliedToastSeq) {
        lastAppliedToastSeq = snapshot.lastToast.seq;
        renderToast(snapshot.lastToast.msg, snapshot.lastToast.kind);
    } else {
        hideToast();
    }
    renderAll();
    notifyChange();
}

function mount(container_, { players, gameId, onChange, multiplier, turnMode: turnModeOption, observer = false }) {
    container = container_;
    currentPlayers = players;
    onChangeCallback = onChange;
    isObserver = observer;
    multiplyScores = (multiplier || configRowDefault(snooker, 'multiplier')) === 'multiply';
    turnMode = turnModeOption || configRowDefault(snooker, 'turnMode');

    scores = {};
    potHistory = {};
    players.forEach(p => { scores[p] = 0; potHistory[p] = []; });
    redsRemaining = TOTAL_REDS;
    removedReds = [];
    awaitingColor = false;
    nextColorIndex = 0;
    phase = 'reds';
    activeIndex = 0;
    dartsThisTurn = 0;
    turnDarts = [];
    turnStartScore = 0;
    matchComplete = false;
    history = [];
    toastSeq = 0;
    lastToast = null;
    lastAppliedToastSeq = 0;

    container.innerHTML = boardScreenHTML(players, isObserver);
    if (!isObserver) {
        document.getElementById('snookerBoardUndoBtn').addEventListener('click', undoLastDart);
        document.getElementById('snookerEndTurnBtn').addEventListener('click', endTurn);
    }
    renderAll();
    // No notifyChange() here, unlike a plain new-game render - every other
    // controller in this codebase (Killer, x01, the closing-game family)
    // leaves mount() silent on this front for the same reason: app.js's
    // remountCurrentGameForObserverChange() calls mount() to reset state and
    // then immediately setState() to restore the real in-progress match, and
    // setState() already calls notifyChange() itself once that's done. A
    // notifyChange() here fired first, mid-remount, with the still-fresh
    // reset state - which, in a synced session, raced (and, since
    // sync.pushEnvelope() is async, usually beat) the correct push from
    // setState(), broadcasting a mid-match game as if it had just restarted
    // to every other client the moment an observer switched to playing.
}

export default { definition: snooker, mount, hasProgress, getState, setState };
